import React, { useMemo, useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { parseISO, isAfter, isBefore, subDays } from "date-fns";
import {
  fetchPaymentsData,
  fetchPaymentsReport,
} from "../../services/dashboard.service";
import { getUser } from "../../lib/authToken";
import "./payments.css";


function SummaryCard({ label, value, hint, tone }) {
  return (
    <div className={`pt-card ${tone ? `pt-card--${tone}` : ""}`}>
      <div className="pt-card__label">{label}</div>
      <div className="pt-card__value">{value}</div>
      {hint ? <div className="pt-card__hint">{hint}</div> : null}
    </div>
  );
}

function FilterBar({
  query,
  setQuery,
  range,
  setRange,
  onApply,
  method,
  setMethod,
  status,
  setStatus,
  onReset,
  onReports,
}) {
  return (
    <div className="pt-filters">
      {/* Left group */}
      <div className="pt-filters-left">
        <input
          className="pt-input"
          placeholder="Search invoice or customer…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className="pt-field">
          <label>From</label>
          <input
            type="datetime-local"
            value={range.start}
            onChange={(e) => setRange({ ...range, start: e.target.value })}
          />
        </div>

        <div className="pt-field">
          <label>To</label>
          <input
            type="datetime-local"
            value={range.end}
            onChange={(e) => setRange({ ...range, end: e.target.value })}
          />
        </div>

        <div className="pt-field">
          <label>Method</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">All</option>
            <option>Card</option>
            <option>UPI</option>
            <option>NetBanking</option>
          </select>
        </div>

        <div className="pt-field">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option>Completed</option>
            <option>Pending</option>
            <option>Failed</option>
            <option>Refunded</option>
            <option>Settled</option>
          </select>
        </div>
      </div>

      <div className="pt-filters-right">
        <button className="pt-btn" onClick={onApply}>
          Apply
        </button>
        <button className="pt-btn pt-btn--ghost" onClick={onReset}>
          Reset
        </button>
        <button className="pt-btn pt-btn--secondary" onClick={onReports}>
          Reports
        </button>
      </div>
    </div>
  );
}

function TransactionsTable({ rows }) {
  return (
    <div className="pt-table__wrap">
      <table className="pt-table">
        <thead>
          <tr>
            <th>Invoice</th>
            <th>Customer</th>
            <th>Method</th>
            <th>Status</th>
            <th className="tar">Amount (₹)</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.id}</td>
              <td>{r.customer}</td>
              <td>{r.method}</td>
              <td>
                <span className={`pt-pill pt-pill--${r.status.toLowerCase()}`}>
                  {r.status}
                </span>
              </td>
              <td className="tar">
                {r.amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </td>
              <td>{r.date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------- ECharts components -------
function MethodBreakdownChart({ data }) {
  const option = React.useMemo(
    () => ({
      color: ["#ffae42", "#ffe0c2"],

      tooltip: { trigger: "item" },
      legend: { top: "bottom", textStyle: { color: "#111111" } },
      series: [
        {
          name: "By Method",
          type: "pie",
          radius: ["40%", "70%"],
          itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
          label: { color: "#111111" },
          data: data.map((d, i) => ({
            name: d.method,
            value: d.total,

            itemStyle: { color: i % 2 === 0 ? "#ffae42" : "#363333ff" },
          })),
        },
      ],
    }),
    [data]
  );

  return (
    <div className="pt-card pt-card--chart">
      <div className="pt-card__label">Method Breakdown</div>
      <ReactECharts
        option={option}
        style={{ height: 260 }}
        notMerge
        lazyUpdate
      />
    </div>
  );
}

function SuccessFailChart({ labels = [], success = [], failed = [] }) {
  const option = React.useMemo(
    () => ({
      grid: { left: 40, right: 16, top: 24, bottom: 66, containLabel: true },
      tooltip: { trigger: "axis" },
      legend: {
        bottom: 5,
        itemWidth: 15,
        textStyle: { color: "#111111" },
        data: ["Success", "Failed"],
      },
      xAxis: {
        type: "category",
        data: labels,
        boundaryGap: true,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#111111" } },
        axisLabel: { color: "#111111", margin: 5 },
      },
      yAxis: {
        type: "value",
        splitLine: { lineStyle: { color: "#f0e2d6" } },
        axisLine: { show: false },
        axisLabel: { color: "#111111" },
      },
      series: [
        {
          name: "Success",
          type: "bar",
          stack: "tot",
          data: success,
          barMaxWidth: 30,
          itemStyle: { color: "#ff7a00" },
        },
        {
          name: "Failed",
          type: "bar",
          stack: "tot",
          data: failed,
          barMaxWidth: 26,
          itemStyle: { color: "#111111" },
        },
      ],
    }),
    [labels, success, failed]
  );

  return (
    <div className="pt-card pt-card--chart">
      <div className="pt-card__label">Successful vs Failed Transactions</div>
      <ReactECharts
        option={option}
        style={{ height: 260 }}
        notMerge
        lazyUpdate
      />
    </div>
  );
}

export default function PaymentsTab() {
  const [toast, setToast] = useState(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState("");
  const [method, setMethod] = useState("");
  const [status, setStatus] = useState("Completed");
  const today = new Date();
  const thirtyDaysAgo = subDays(today, 30);
  // Format with time for datetime-local input (YYYY-MM-DDTHH:mm)
  const formatWithTime = (date, includeTime = false) => {
    const d = new Date(date);
    const dateStr = d.toISOString().split("T")[0];
    const timeStr = includeTime ? d.toTimeString().slice(0, 5) : "00:00";
    return `${dateStr}T${timeStr}`;
  };
  const [range, setRange] = useState({
    start: formatWithTime(thirtyDaysAgo),
    end: formatWithTime(today, true),
  });
  const [appliedRange, setAppliedRange] = useState({
    start: formatWithTime(thirtyDaysAgo),
    end: formatWithTime(today, true),
  });

  const loadData = async (fromDate, toDate, methodFilter, statusFilter) => {
    try {
      setLoading(true);
      const result = await fetchPaymentsData({
        from_date: fromDate,
        to_date: toDate,
        method: methodFilter,
        status: statusFilter,
      });
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load initial data with default date range (30 days ago to today) and default filters
    const fromDate = appliedRange.start;
    const toDate = appliedRange.end;
    const methodFilter = method || "All";
    const statusFilter = status || "All";
    loadData(fromDate, toDate, methodFilter, statusFilter);
  }, [appliedRange.end, appliedRange.start, method, status]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const months = data?.months || [];
  const success = data?.success || [];
  const failed = data?.failed || [];

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const q = query.trim().toLowerCase();
      const hit =
        !q ||
        r.id.toLowerCase().includes(q) ||
        r.customer.toLowerCase().includes(q);

      const inMethod = !method || r.method === method;
      const inStatus = !status || r.status === status;

      const hasStart = !!appliedRange.start;
      const hasEnd = !!appliedRange.end;
      // Parse date from 'dd MMM yyyy' format (e.g., "03 Nov 2025")
      // Also parse time if present in the date string (e.g., "03 Nov 2025 14:30")
      const dateParts = r.date.split(" ");
      const day = parseInt(dateParts[0], 10);
      const monthStr = dateParts[1];
      const year = parseInt(dateParts[2], 10);
      
      // Check if time is present in the date string (format: "dd MMM yyyy HH:mm")
      let hour = 0;
      let minute = 0;
      if (dateParts[3]) {
        const timeParts = dateParts[3].split(":");
        hour = parseInt(timeParts[0], 10);
        minute = parseInt(timeParts[1], 10);
      }

      const monthMap = {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
      };
      const month = monthMap[monthStr];
      const d = new Date(year, month, day, hour, minute);
      const startOk = !hasStart || !isBefore(d, parseISO(appliedRange.start));
      const endOk = !hasEnd || !isAfter(d, parseISO(appliedRange.end));

      return hit && inMethod && inStatus && startOk && endOk;
    });
  }, [rows, query, method, status, appliedRange]);

  const totals = useMemo(() => {
    const gross = filtered.reduce((a, b) => a + b.amount, 0);
    const completed = filtered
      .filter((x) => x.status === "Completed" || x.status === "Settled")
      .reduce((a, b) => a + b.amount, 0);
    const pending = filtered
      .filter((x) => x.status === "Pending")
      .reduce((a, b) => a + b.amount, 0);
    return { gross, completed, pending };
  }, [filtered]);

  const methodAgg = useMemo(() => {
    const by = {};
    filtered.forEach((r) => {
      by[r.method] = (by[r.method] || 0) + r.amount;
    });
    return Object.entries(by).map(([method, total]) => ({ method, total }));
  }, [filtered]);

  function handleReset() {
    setQuery("");
    setMethod("");
    setStatus("");
    const today = new Date();
    const thirtyDaysAgo = subDays(today, 30);
    setRange({
      start: formatWithTime(thirtyDaysAgo),
      end: formatWithTime(today, true),
    });
    setAppliedRange({
      start: formatWithTime(thirtyDaysAgo),
      end: formatWithTime(today, true),
    });
    // Reload data with default date range and default filters
    loadData(
      formatWithTime(thirtyDaysAgo),
      formatWithTime(today, true),
      "All",
      "All"
    );
  }

  function handleApplyDates() {
    setAppliedRange(range);
    // Reload data with new date range
    const fromDate = range.start || new Date().toISOString().split("T")[0] + "T00:00";
    const toDate = range.end || new Date().toISOString().split("T")[0] + "T23:59";
    loadData(fromDate, toDate, method, status);
  }

  const handleReports = async () => {
    try {
      const user = getUser();
      const payload = {
        org_id: user.restaurant_id,
        location: user.location,
        from_date: appliedRange.start,
        to_date: appliedRange.end,
        method: method || "All",
        status: status || "All",
      };

      const response = await fetchPaymentsReport(payload);

      // ✅ SHOW SUCCESS POPUP
      setToast({
        type: "success",
        message: response.message || "Report generated successfully",
      });

      // auto hide after 3 seconds
      setTimeout(() => setToast(null), 3000);
    } catch (error) {
      console.error("Error generating report:", error);

      // ❌ SHOW ERROR POPUP
      setToast({
        type: "error",
        message: "Failed to generate report. Please try again.",
      });

      setTimeout(() => setToast(null), 3000);
    }
  };

  if (loading) {
    return <div className="pt-root">Loading payments data...</div>;
  }

  if (error) {
    return <div className="pt-root">Error loading payments data: {error}</div>;
  }

  return (
    <div className="pt-root">
      {toast && (
      <div className={`pt-toast pt-toast--${toast.type}`}>
        {toast.message}
      </div>
    )}
      <FilterBar
        query={query}
        setQuery={setQuery}
        range={range}
        setRange={setRange}
        onApply={handleApplyDates}
        method={method}
        setMethod={setMethod}
        status={status}
        setStatus={setStatus}
        onReset={handleReset}
        onReports={handleReports}
      />
      <div className="pt-stats">
        <SummaryCard
          label="Total Payments"
          value={`₹${totals.gross.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
          })}`}
        />
        <SummaryCard
          label="Completed/Settled"
          value={`₹${totals.completed.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
          })}`}
          tone="ok"
        />
        <SummaryCard
          label="Pending"
          value={`₹${totals.pending.toLocaleString("en-IN", {
            maximumFractionDigits: 2,
          })}`}
          tone="warn"
        />
      </div>

      <div className="pt-grid">
        <SuccessFailChart labels={months} success={success} failed={failed} />
        <MethodBreakdownChart data={methodAgg} />
      </div>

      <TransactionsTable rows={filtered} />
    </div>
  );
}
